import { useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'

import LoadingBox from 'components/Common/Loading/LoadingBox'
import { breakpointSizes } from 'components/utils/breakpoint'
import { useMapboxContext } from 'contexts/MapboxContext'

import { buildStaticMapUrls } from './staticMapUrl'
import type { StaticPropertyMapProps } from './types'

const StaticMapRoot = styled.div`
  width: 100%;
`

const StaticMapFrame = styled.div`
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const StaticMapImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const StaticMapPicture = styled.picture`
  display: block;
  width: 100%;
  height: 100%;
`

const StaticMapPlaceholder = styled(LoadingBox)`
  width: 100%;
  height: 100%;
`

function StaticPropertyMapContent(props: StaticPropertyMapProps) {
  const {
    className,
    focusOnFirstMarker,
    height,
    highlightedMarkerId,
    isLoading = false,
    markers,
    wrapperStyle,
  } = props
  const { accessToken } = useMapboxContext()
  // Keep height out of styled-components to avoid generating dynamic classes for per-instance map sizes.
  const rootStyle = useMemo<React.CSSProperties>(
    () => ({
      height,
      ...wrapperStyle,
    }),
    [height, wrapperStyle],
  )

  const staticMapUrls = useMemo(() => {
    if (isLoading || !accessToken) {
      return undefined
    }

    return buildStaticMapUrls({
      accessToken,
      focusOnFirstMarker,
      height,
      highlightedMarkerId,
      markers,
    })
  }, [
    accessToken,
    focusOnFirstMarker,
    height,
    highlightedMarkerId,
    isLoading,
    markers,
  ])
  const staticMapContent = !isLoading && staticMapUrls ? (
    <StaticMapPicture>
      <source
        media={`(max-width: ${breakpointSizes.mobile.max}px)`}
        srcSet={staticMapUrls.mobileUrl}
      />
      <source
        media={`(max-width: ${breakpointSizes.tablet.max}px)`}
        srcSet={staticMapUrls.tabletUrl}
      />
      <source
        media={`(max-width: ${breakpointSizes.desktop.max}px)`}
        srcSet={staticMapUrls.desktopUrl}
      />
      <StaticMapImage
        alt=""
        src={staticMapUrls.largeDesktopUrl}
        loading="lazy"
        decoding="async"
        draggable={false}
      />
    </StaticMapPicture>
  ) : (
    <StaticMapPlaceholder
      data-testid="static-property-map-placeholder"
      borderRadius="S"
    />
  )

  return (
    <StaticMapRoot
      className={className}
      style={rootStyle}
    >
      <StaticMapFrame>
        {staticMapContent}
      </StaticMapFrame>
    </StaticMapRoot>
  )
}

export default StaticPropertyMapContent
